import { fbt } from 'fbtee'
import { Heart, CircleX, Check } from 'lucide-react'
import { DropdownMenuItem } from './ui/dropdown-menu'
import { cn } from '../lib/utils'
import type { EventInterestStatus } from '../hooks/useUserEventInterests'
import { GoingCheckIcon } from './GoingCheckIcon'

/**
 * The three answers to "are you coming", shared by every dropdown that sets
 * event attendance, ordered by how committed each one is. Picking the current
 * answer again clears it, so you can drop an RSVP without having to claim
 * "Not going". Icons keep their colour whether or not they're the current pick,
 * so the row reads as a legend for the badges elsewhere in the app.
 */
export function EventInterestMenuItems({
  status,
  onSetStatus,
  size = 'default',
}: {
  status: EventInterestStatus
  onSetStatus: (status: EventInterestStatus) => void
  /** `lg` gives each answer a roomier, easier-to-tap row (party page RSVP). */
  size?: 'default' | 'lg'
}) {
  const itemClass = size === 'lg' ? 'gap-3 px-2 py-2.5 text-base' : undefined
  const iconClass = size === 'lg' ? 'size-5' : undefined
  const checkClass = size === 'lg' ? 'ml-auto size-4' : 'ml-auto size-3.5'
  const toggle = (next: EventInterestStatus) => onSetStatus(status === next ? 'none' : next)
  return (
    <>
      <DropdownMenuItem className={itemClass} onSelect={() => toggle('going')}>
        <GoingCheckIcon className={iconClass} />
        {fbt('Going', 'Menu item to mark going to an event')}
        {status === 'going' && <Check className={checkClass} />}
      </DropdownMenuItem>
      <DropdownMenuItem className={itemClass} onSelect={() => toggle('interested')}>
        <Heart className={cn('fill-interested text-interested', iconClass)} />
        <fbt desc="Menu item to mark interest in an event">Interested</fbt>
        {status === 'interested' && <Check className={checkClass} />}
      </DropdownMenuItem>
      <DropdownMenuItem className={itemClass} onSelect={() => toggle('not_going')}>
        <CircleX className={cn('text-muted-foreground', iconClass)} />
        <fbt desc="Menu item to mark that the user is not going to an event">Not going</fbt>
        {status === 'not_going' && <Check className={checkClass} />}
      </DropdownMenuItem>
    </>
  )
}
